/**
 * Русские числительные.
 *
 * ⚠️ Переехал из `features/plans/` 18.08.2026: там он лежал по историческим
 * причинам, а счётчики из него брали архив, карточка ученика и форма выдачи
 * домашки. Переезд откладывался до второй такой утилиты, и она появилась
 * (`lib/when.ts`, относительное время списков), причём с зависимостью отсюда:
 * импорт из `lib/` в `features/` нормален, обратный нет.
 *
 * Отдельный модуль, потому что одни и те же счётчики показывают три места:
 * карточка заготовки в списке, шапка редактора и экран «Уроки по этой
 * заготовке». Разъехавшиеся копии дают «12 урок» на одном экране и «12 уроков»
 * на соседнем, а замечает это ровно тот человек, которому мы это показываем.
 */

/** Форма слова по числу: 1 урок, 2 урока, 5 уроков. */
export function pick(count: number, one: string, few: string, many: string): string {
  const last = count % 10;
  const tens = count % 100;
  // 11-14 всегда «уроков», хотя последняя цифра говорит обратное
  if (tens >= 11 && tens <= 14) return many;
  if (last === 1) return one;
  if (last >= 2 && last <= 4) return few;
  return many;
}

export function slideCountText(count: number): string {
  return `${count} ${pick(count, "слайд", "слайда", "слайдов")}`;
}

/**
 * «ещё один слайд», «ещё 2 слайда», «ещё 5 слайдов»: сколько слайдов заведётся
 * под вопросы к записи (раздел 8 docs/listening-questions-design.md).
 *
 * ⚠️ При единице число НЕ пишется, как в `unopenedHomeworkText`: «ещё 1 слайд»
 * это машинный перевод, вслух так не говорят. При 21 число пишется, а форма
 * остаётся единственной, потому что форму выбирает последняя цифра.
 */
export function moreSlidesText(count: number): string {
  const form = pick(count, "слайд", "слайда", "слайдов");
  return count === 1 ? `ещё один ${form}` : `ещё ${count} ${form}`;
}

export function lessonCountText(count: number): string {
  return `${count} ${pick(count, "урок", "урока", "уроков")}`;
}

export function meetingCountText(count: number): string {
  return `${count} ${pick(count, "занятие", "занятия", "занятий")}`;
}

/**
 * «1 занятие ещё не взяло материал этой заготовки…»: вопрос перед удалением
 * заготовки, у которой есть занятия, не открывавшие доску ни разу.
 *
 * ⚠️ Согласуется по числу не только существительное, но и сказуемое с
 * местоимением, и все три формы выбирает ОДНА функция по последней цифре. Иначе
 * при двадцати одном занятии выходит «21 занятие ещё не взяли»: существительное
 * в единственном, а глагол во множественном, потому что глагол сравнивали с
 * единицей.
 */
export function pendingMeetingsText(count: number): string {
  const took = pick(count, "взяло", "взяли", "взяли");
  const empty = pick(
    count,
    "оно откроется пустым",
    "они откроются пустыми",
    "они откроются пустыми",
  );
  return `${meetingCountText(count)} ещё не ${took} материал этой заготовки. Если удалить её сейчас, ${empty}`;
}

export function studentCountText(count: number): string {
  return `${count} ${pick(count, "ученик", "ученика", "учеников")}`;
}

/**
 * Счётчики у заголовка экрана: «6 серий», «5 групп», «12 заготовок».
 *
 * ⚠️ Считается СОДЕРЖИМОЕ экрана, а не работа преподавателя (решение владельца
 * 23.08.2026, раздел 5.10 CLAUDE.md). «6 серий» отвечает на вопрос «сколько их
 * тут», и в этом же ряду стоят «24 ученика» и «22 занятия»; счётчик вида
 * «заведено за месяц» сюда не попадёт ни под каким видом.
 */
export function seriesCountText(count: number): string {
  return `${count} ${pick(count, "серия", "серии", "серий")}`;
}

export function groupCountText(count: number): string {
  return `${count} ${pick(count, "группа", "группы", "групп")}`;
}

export function planCountText(count: number): string {
  return `${count} ${pick(count, "заготовка", "заготовки", "заготовок")}`;
}

/**
 * «Задать 3 домашки», «Задать 12 домашек», «Задать 21 домашку»: винительный
 * падеж, кнопка формы, которая задаёт домашку нескольким ученикам сразу.
 */
export function homeworkCountText(count: number): string {
  return `${count} ${pick(count, "домашку", "домашки", "домашек")}`;
}

/** «3 задания соберутся из 2 генераторов». */
export function exerciseCountText(count: number): string {
  return `${count} ${pick(count, "задание", "задания", "заданий")}`;
}

/**
 * «не больше 1 цифры», «не больше 8 цифр»: потолок цифр в числе столбика.
 * Родительный после «не больше», поэтому оборот целиком внутри функции, по той
 * же причине, что у `fromGeneratorsText`.
 */
export function atMostDigitsText(count: number): string {
  return `не больше ${count} ${pick(count, "цифры", "цифр", "цифр")}`;
}

/**
 * «из 1 генератора», «из 2 генераторов»: родительный падеж после предлога, а не
 * именительный. Предлог внутри функции намеренно: без него следующий вызывающий
 * подставит эти же формы туда, где нужны другие.
 */
export function fromGeneratorsText(count: number): string {
  return `из ${count} ${pick(count, "генератора", "генераторов", "генераторов")}`;
}

/**
 * «по 1 заданию», «по 34 заданиям»: дательный падеж после предлога, подпись
 * шапки панели «Где дыры». Предлог внутри функции по той же причине, что у
 * `fromGeneratorsText`: без него следующий вызывающий подставит дательный туда,
 * где нужен именительный, и получит «34 заданиям» посреди строки.
 */
export function byTasksText(count: number): string {
  return `по ${count} ${pick(count, "заданию", "заданиям", "заданиям")}`;
}

/**
 * «по 1 теме», «по 11 темам»: дательный падеж после предлога, состав собранного
 * теста на весь курс («13 заданий по 11 темам»). Предлог внутри функции по той
 * же причине, что у `byTasksText`.
 */
export function byTopicsText(count: number): string {
  return `по ${count} ${pick(count, "теме", "темам", "темам")}`;
}

/**
 * «1 тема», «3 темы», «11 тем»: именительный падеж, счёт тем в свёрнутом хвосте
 * списка спуска у демо на странице обзора («Ещё 4 темы ниже по карте»).
 *
 * ⚠️ Именительный, а не дательный у `byTopicsText` строкой выше («по 11 темам»):
 * та стоит после предлога, эта после слова «ещё». Две формы одного слова живут
 * порознь по той же причине, что у пары `forMinutesText` и `minuteCountText`:
 * иначе следующий вызывающий подставит падеж не туда.
 */
export function topicCountText(count: number): string {
  return `${count} ${pick(count, "тема", "темы", "тем")}`;
}

/**
 * «на 1 минуту», «на 20 минут»: винительный падеж после предлога. Читателей
 * два: подпись под блоком «Тест на весь курс» («Примерно на 20 минут») и имена
 * готовых кнопок таймера занятия для озвучки («Таймер на 3 минуты»). Предлог
 * внутри функции по той же причине, что у `byTasksText`.
 *
 * ⚠️ Число приходит доводом, а не пишется в строке словами: двадцать минут у
 * теста это константа контракта (`COURSE_TEST_MINUTES`), а у таймера числа
 * стоят рядом списком (`TIMER_PRESET_MINUTES`), и второе значение обязано
 * стоить одной строки. Зашитое в текст «20 минут» соврало бы молча.
 */
export function forMinutesText(count: number): string {
  return `на ${count} ${pick(count, "минуту", "минуты", "минут")}`;
}

/**
 * «1 день», «3 дня», «5 дней»: срочность в списках кабинета (`lib/when.ts`).
 *
 * Здесь же, а не в `when.ts`, по той же причине, по которой этот модуль вообще
 * существует: формы числительных живут в одном месте, иначе разъезжаются.
 */
export function dayCountText(count: number): string {
  return `${count} ${pick(count, "день", "дня", "дней")}`;
}

/**
 * «в 1 правиле», «в 6 правилах»: предложный падеж, панель тега ошибки на карте
 * русского. Предлог внутри функции по той же причине, что у `fromGeneratorsText`.
 */
export function inRulesText(count: number): string {
  return `в ${count} ${pick(count, "правиле", "правилах", "правилах")}`;
}

/** «6 разных пробелов», «1 разный пробел»: там же, второй счёт по журналу. */
export function gapCountText(count: number): string {
  return `${count} ${pick(count, "разный пробел", "разных пробела", "разных пробелов")}`;
}

/**
 * «1 знак», «200 знаков»: остаток месячной квоты озвучки.
 *
 * ⚠️ Считаются знаки ТЕКСТА, то есть заказ, а не работа того, кто его собрал
 * (решение владельца 23.08.2026 запрещает считать что-либо в адрес
 * преподавателя). Поэтому и говорится про остаток ресурса, а не про расход.
 */
export function charCountText(count: number): string {
  return `${count} ${pick(count, "знак", "знака", "знаков")}`;
}

/**
 * «1 минута», «5 минут»: запас озвучки, пересчитанный в минуты звука.
 *
 * ⚠️ Именительный падеж, а не «на N минут» соседней функции: та стоит после
 * предлога («примерно на 20 минут»), эта после сказуемого («осталось примерно
 * 49 минут звука»). Две формы одного слова живут порознь ровно затем, чтобы
 * следующий вызывающий не подставил падеж не туда.
 */
export function minuteCountText(count: number): string {
  return `${count} ${pick(count, "минута", "минуты", "минут")}`;
}

/**
 * «1 секунда», «2 секунды», «5 секунд»: остаток таймера занятия словами для
 * читалки («7 минут 42 секунды»). Именительный падеж, пара к `minuteCountText`,
 * и по той же причине отдельно от родительного `fromSecondsText`.
 */
export function secondCountText(count: number): string {
  return `${count} ${pick(count, "секунда", "секунды", "секунд")}`;
}

/**
 * «1 реплика», «12 реплик»: сколько реплик вышло из вставленного диалога.
 *
 * ⚠️ Считается ЗАКАЗ, а не работа того, кто его собрал (решение владельца
 * 23.08.2026). Число отвечает на вопрос «что получится, если нажать», и стоит
 * рядом с кнопкой, а не в отчёте.
 */
export function replicaCountText(count: number): string {
  return `${count} ${pick(count, "реплика", "реплики", "реплик")}`;
}

/**
 * «1 абзац», «3 абзаца», «7 абзацев».
 *
 * ⚠️ Нужен потому, что у записи, озвученной ОДНИМ голосом, строки это абзацы, а
 * не реплики (сплошной текст режется по абзацам с 05.10.2026). Своя форма слова
 * заводится рядом со счётом реплик затем, чтобы два счётчика одного экрана
 * склоняли числа одинаково.
 */
export function paragraphCountText(count: number): string {
  return `${count} ${pick(count, "абзац", "абзаца", "абзацев")}`;
}

/** «1 голос», «3 голоса», «7 голосов»: там же, рядом со счётом реплик. */
export function voiceCountText(count: number): string {
  return `${count} ${pick(count, "голос", "голоса", "голосов")}`;
}

/** «1 правило», «3 правила», «9 правил». */
export function ruleCountText(count: number): string {
  return `${count} ${pick(count, "правило", "правила", "правил")}`;
}

/**
 * «до 1 правила», «до 3 правил»: сколько правил карты берём в один заказ.
 *
 * Родительный падеж после предлога, а не именительный соседней функции, и
 * предлог внутри по той же причине, что у `fromGeneratorsText`: без него
 * следующий вызывающий подставит эти формы туда, где нужны другие.
 */
export function toRulesText(count: number): string {
  return `до ${count} ${pick(count, "правила", "правил", "правил")}`;
}

/*
  ⚠️ **Двух счётчиков запаса тут больше нет** (решение владельца 08.09.2026):
  `composeCountText` («5 сочинений») и `questionSetCountText` («5 наборов
  вопросов»). Оба писали остаток, посчитанный по выдуманной средней цене
  нажатия, а останавливает человека общий кошелёк, и остаток живёт теперь в одном
  месте, полоской в боковой панели кабинета. Заводя такой счётчик снова, сначала
  проверьте, чем он отличается от полоски.
*/

/** «1 вопрос», «3 вопроса», «8 вопросов»: сколько их вышло к записи. */
export function questionCountText(count: number): string {
  return `${count} ${pick(count, "вопрос", "вопроса", "вопросов")}`;
}

/** «для 1 ошибки», «для 3 ошибок»: родительный после предлога. */
export function forErrorsText(count: number): string {
  return `для ${count} ${pick(count, "ошибки", "ошибок", "ошибок")}`;
}

/** «в 1 форме», «в 6 формах»: панель тега ошибки на карте английского. */
export function inFormsText(count: number): string {
  return `в ${count} ${pick(count, "форме", "формах", "формах")}`;
}

/**
 * «домашку», «3 домашки», «5 домашек»: извещение ученику о заданном (просьба
 * преподавателя 20.08.2026), винительный падеж после «Вам задали».
 *
 * ⚠️ Число при единице НЕ пишется: «вам задали домашку» это то, как об этом
 * говорят, а «вам задали 1 домашку» это машинный перевод. При 21 число
 * пишется, а форма остаётся единственной, и это не исключение из правила, а
 * само правило: форму выбирает последняя цифра.
 *
 * ⚠️ **Слова «новое» здесь нет, хотя просьба звучала как «уведомление».**
 * Считаются домашки БЕЗ отметки об открытии, а не заданные недавно, и разойтись
 * этим двум вещам есть где: домашку задали три недели назад, ученик её не
 * открыл, и «вам задали новую домашку» рядом с чипом «просрочена» читалось бы
 * как сообщение про ещё одну, четвёртую. Обещать свежесть может только
 * предикат по времени задания, а его нет.
 *
 * ⚠️ Слово «домашка», а не «домашнее задание» (словарь 13.10.2026): «задание»
 * у нас одно упражнение внутри домашки, и ребёнок читал «3 домашних задания»
 * как три задачи, а это три домашки.
 */
export function unopenedHomeworkText(count: number): string {
  const form = pick(count, "домашку", "домашки", "домашек");
  return count === 1 ? form : `${count} ${form}`;
}

/** «1 предложение», «3 предложения», «20 предложений»: счётчик перевода в заготовке. */
export function sentenceCountText(count: number): string {
  return `${count} ${pick(count, "предложение", "предложения", "предложений")}`;
}

/**
 * «1 картинка», «3 картинки», «5 картинок»: материал домашки
 * (`docs/homework-scratch-design.md`). Счёт того, что уйдёт ученику, а не
 * работы преподавателя.
 */
export function imageCountText(count: number): string {
  return `${count} ${pick(count, "картинка", "картинки", "картинок")}`;
}

/** «1 аудиозапись», «3 аудиозаписи»: строка предпросмотра материала домашки. */
export function audioCountText(count: number): string {
  return `${count} ${pick(count, "аудиозапись", "аудиозаписи", "аудиозаписей")}`;
}

/** «1 видео», «5 видео»: слово не склоняется, функция ради единого вида строки. */
export function videoCountText(count: number): string {
  return `${count} видео`;
}

/**
 * «от 1 секунды», «от 30 секунд»: родительный падеж после предлога, нижняя
 * граница своего времени в панели таймера. Предлог внутри функции по той же
 * причине, что у `byTasksText`. У родительного форм две, а не три: «от 2
 * секунд» и «от 5 секунд» совпадают.
 */
export function fromSecondsText(count: number): string {
  return `от ${count} ${pick(count, "секунды", "секунд", "секунд")}`;
}

/** «до 1 часа», «до 3 часов»: верхняя граница своего времени в панели таймера. */
export function toHoursText(count: number): string {
  return `до ${count} ${pick(count, "часа", "часов", "часов")}`;
}

/** «12 слов»: поле «Слова занятия» и список колоды (10.3 `docs/flashcards-design.md`). */
export function wordCountText(count: number): string {
  return `${count} ${pick(count, "слово", "слова", "слов")}`;
}

/** «у 3 учеников»: удаление колоды, которая у кого-то открыта (10.3). */
export function atStudentsText(count: number): string {
  return `у ${count} ${pick(count, "ученика", "учеников", "учеников")}`;
}

/** «через 40 минут»: когда следующие карточки (10.3). Винительный, как у «на N минут». */
export function afterMinutesText(count: number): string {
  return `через ${count} ${pick(count, "минуту", "минуты", "минут")}`;
}

export function afterHoursText(count: number): string {
  return `через ${count} ${pick(count, "час", "часа", "часов")}`;
}

export function afterDaysText(count: number): string {
  return `через ${count} ${pick(count, "день", "дня", "дней")}`;
}

/** «12 карточек»: предпросмотр вставки в колоду (8.1). */
export function cardCountText(count: number): string {
  return `${count} ${pick(count, "карточка", "карточки", "карточек")}`;
}

/**
 * «длиннее 300 знаков»: родительный падеж после сравнительной степени. После
 * числа на единицу единственное («длиннее 21 знака»), иначе множественное.
 */
export function longerThanCharsText(count: number): string {
  return `длиннее ${count} ${pick(count, "знака", "знаков", "знаков")}`;
}
